import { nanoid } from 'nanoid';

import type { PostTypeDataMap, ReadyToSharePlatformPostType } from '#src/app/pages/starterDashboardV2/shared/hooks';
import type { EnrichedReadyToSharePlatformPostV2 } from '#src/app/shared/api/readyToShare';

import type { PlatformType, RecommendationsData } from '../utils/platformGrouping';
import { ALL_AVAILABLE_PLATFORMS, isPlatformType } from '../utils/platformGrouping';

type LoadedDisplayablePost = {
	state: 'loaded';
	postTypeName: EnrichedReadyToSharePlatformPostV2['__typename'];
	post: EnrichedReadyToSharePlatformPostV2;
	key: string;
};

type SkeletonDisplayablePost = {
	state: 'skeleton';
	postTypeName: EnrichedReadyToSharePlatformPostV2['__typename'];
	key: string;
};

type RetryDisplayablePost = {
	state: 'retry';
	postTypeName: EnrichedReadyToSharePlatformPostV2['__typename'];
	key: string;
};

export type DisplayablePost = LoadedDisplayablePost | SkeletonDisplayablePost | RetryDisplayablePost;

const platformTypenameMapping: Readonly<Record<PlatformType, Array<EnrichedReadyToSharePlatformPostV2['__typename']>>> =
	{
		facebook: ['ReadyToShareFacebookPost', 'ReadyToShareFacebookStory', 'ReadyToShareFacebookGroupsPost'],
		instagram: ['ReadyToShareInstagramPost', 'ReadyToShareInstagramStory'],
		reddit: ['ReadyToShareRedditGroupsPost'],
	};

const TYPENAME_TO_POST_TYPE: Readonly<
	Record<EnrichedReadyToSharePlatformPostV2['__typename'], ReadyToSharePlatformPostType>
> = {
	ReadyToShareFacebookPost: 'FACEBOOK_POST',
	ReadyToShareFacebookStory: 'FACEBOOK_STORY',
	ReadyToShareFacebookGroupsPost: 'FACEBOOK_GROUPS_POST',
	ReadyToShareInstagramPost: 'INSTAGRAM_POST',
	ReadyToShareInstagramStory: 'INSTAGRAM_STORY',
	ReadyToShareRedditGroupsPost: 'REDDIT_GROUPS_POST',
};

const typenameWeights: Readonly<Record<EnrichedReadyToSharePlatformPostV2['__typename'], number>> = {
	ReadyToShareFacebookPost: 100,
	ReadyToShareRedditGroupsPost: 90,
	ReadyToShareFacebookGroupsPost: 80,
	ReadyToShareInstagramStory: 70,
	ReadyToShareFacebookStory: 60,
	ReadyToShareInstagramPost: 50,
};

const mobileTypenameWeights: Readonly<Record<EnrichedReadyToSharePlatformPostV2['__typename'], number>> = {
	ReadyToShareFacebookPost: 100,
	ReadyToShareInstagramStory: 90,
	ReadyToShareRedditGroupsPost: 80,
	ReadyToShareFacebookGroupsPost: 70,
	ReadyToShareFacebookStory: 60,
	ReadyToShareInstagramPost: 50,
};

function isRedditAvailable(recommendationsData: RecommendationsData): boolean {
	return recommendationsData.isLoading || recommendationsData.hasRedditGroups;
}

function getTypenamesForPlatform(
	platform: PlatformType,
	recommendationsData: RecommendationsData,
): Array<EnrichedReadyToSharePlatformPostV2['__typename']> {
	if (platform === 'reddit' && !isRedditAvailable(recommendationsData)) {
		return [];
	}
	return platformTypenameMapping[platform].filter((typename) => {
		if (typename === 'ReadyToShareFacebookGroupsPost') {
			return recommendationsData.isLoading || recommendationsData.hasFacebookGroups;
		}
		return true;
	});
}

function toDisplayablePost(
	postTypeName: EnrichedReadyToSharePlatformPostV2['__typename'],
	platform: PlatformType,
	postTypeData: PostTypeDataMap,
	recommendationsData: RecommendationsData,
): DisplayablePost {
	const isGroupLoading =
		(postTypeName === 'ReadyToShareFacebookGroupsPost' || platform === 'reddit') && recommendationsData.isLoading;

	if (isGroupLoading) {
		return { state: 'skeleton', postTypeName, key: `${postTypeName}-skeleton-${nanoid()}` };
	}

	const entry = postTypeData[TYPENAME_TO_POST_TYPE[postTypeName]];
	if (!entry || entry.status === 'PENDING') {
		return { state: 'skeleton', postTypeName, key: `${postTypeName}-skeleton-${nanoid()}` };
	}

	if (entry.status === 'FAILED' || entry.status === 'NOT_AVAILABLE') {
		return { state: 'retry', postTypeName, key: `${postTypeName}-retry-${nanoid()}` };
	}

	const validPost = entry.posts.find((post) => !('hasImage' in post) || post.hasImage);
	if (validPost) {
		return { state: 'loaded', postTypeName, post: validPost, key: `${postTypeName}-0` };
	}
	return { state: 'retry', postTypeName, key: `${postTypeName}-retry-${nanoid()}` };
}

function getPostsForPlatform(
	platform: PlatformType,
	postTypeData: PostTypeDataMap,
	recommendationsData: RecommendationsData,
): DisplayablePost[] {
	return getTypenamesForPlatform(platform, recommendationsData).map((typename) =>
		toDisplayablePost(typename, platform, postTypeData, recommendationsData),
	);
}

function sortPosts(posts: DisplayablePost[], isMobile: boolean): DisplayablePost[] {
	const weights = isMobile ? mobileTypenameWeights : typenameWeights;
	return [...posts].sort((a, b) => weights[b.postTypeName] - weights[a.postTypeName]);
}

type BuildDisplayablePostsOptions = {
	postTypeData: PostTypeDataMap;
	recommendationsData: RecommendationsData;
	isMobile: boolean;
};

export function buildDisplayablePosts(
	selectedPlatform: PlatformType | undefined,
	availablePlatforms: PlatformType[],
	{ postTypeData, recommendationsData, isMobile }: BuildDisplayablePostsOptions,
): DisplayablePost[] {
	const platforms = selectedPlatform ? [selectedPlatform] : availablePlatforms;
	const posts = platforms.flatMap((platform) => getPostsForPlatform(platform, postTypeData, recommendationsData));
	return sortPosts(posts, isMobile);
}

export function computeAvailablePlatforms(
	postTypeData: PostTypeDataMap,
	recommendationsData: RecommendationsData,
): PlatformType[] {
	return ALL_AVAILABLE_PLATFORMS.filter((platform) => {
		if (platform === 'reddit' && !isRedditAvailable(recommendationsData)) {
			return false;
		}
		return platformTypenameMapping[platform]
			.map((typename) => TYPENAME_TO_POST_TYPE[typename])
			.some((pt) => !!postTypeData[pt]);
	});
}

export function getInitialPlatform(searchParams: URLSearchParams): PlatformType | undefined {
	const platformParam = searchParams.get('platform');
	return platformParam && isPlatformType(platformParam) ? platformParam : undefined;
}
